// Regras puras da tela /reports/api: filtros, rótulos de resultado, formatos
// e colunas do export. Sem React, sem API — testável em isolamento.
import { format, subDays } from "date-fns";
import { i18n } from "../../translate/i18n";

const t = (k, o) => i18n.t(`aeros.apiReport.${k}`, o);
const iso = (d) => format(d, "yyyy-MM-dd");

export const DEFAULT_FILTERS = (now = new Date()) => ({
  dateFrom: iso(subDays(now, 6)),
  dateTo: iso(now),
  outcome: [],
  whatsappId: "",
  origin: "",
  kind: "",
  route: "",
  channel: "",
  search: "",
});

export const hasActiveFilters = (f) =>
  Boolean(f.outcome.length || f.whatsappId || f.origin || f.kind || f.route || f.channel || f.search);

export const paramsOf = (f) => ({
  dateFrom: f.dateFrom,
  dateTo: f.dateTo,
  outcome: f.outcome.length ? f.outcome.join(",") : undefined,
  whatsappId: f.whatsappId || undefined,
  origin: f.origin || undefined,
  kind: f.kind || undefined,
  route: f.route || undefined,
  channel: f.channel || undefined,
  search: f.search || undefined,
});

const pill = (key, field, tone) => ({ key, field, tone, get label() { return t(`pills.${key || "all"}`); } });
export const OUTCOME_PILLS = [
  pill("", "total"),
  pill("success", "success", "success"),
  pill("queued", "queued", "info"),
  pill("rejected", "rejected", "warning"),
  pill("error", "error", "error"),
  pill("failed", "failed", "error"),
];
const OUTCOME_TONES = { success: "success", sent: "success", queued: "info", rejected: "warning", error: "error", failed: "error" };
export const outcomeMeta = (key) =>
  OUTCOME_TONES[key] ? { key, tone: OUTCOME_TONES[key], label: t(`pills.${key}`) } : { key: "", tone: "muted", label: key || "—" };

const opt = (ns, key) => ({ key, get label() { return t(`${ns}.${key}`); } });
export const KINDS = ["text", "media", "template", "checkNumber", "sms", "smsStatus", "smsBalance", "messageStatus"].map((k) => opt("kinds", k));
export const ROUTES = ["send", "send/linkImage", "checkNumber", "sms/send", "sms/:id", "sms/balance", "messages/:id/status"].map((k) => ({ key: k, label: k }));
export const CHANNELS = ["whatsapp", "whatsapp_oficial", "sms"].map((k) => opt("channels", k));
export const kindLabel = (k) => (k ? t(`kinds.${k}`) : "—");
export const channelLabel = (c) => (c ? t(`channels.${c}`) : "—");
export const originLabel = (o) => (o && o !== "—" ? o : t("noOrigin"));

export const formatMs = (ms) => {
  if (ms == null || Number.isNaN(Number(ms))) return "—";
  const n = Number(ms);
  return n < 1000 ? `${Math.round(n)} ms` : `${(n / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} s`;
};

export const successRate = (c) => (c && Number(c.total) ? `${Math.round((Number(c.success) / Number(c.total)) * 100)}%` : "—");

export const chartRows = (byDay) =>
  (byDay || []).map((d) => ({
    day: String(d.day).slice(5).split("-").reverse().join("/"),
    success: Number(d.success) || 0,
    queued: Number(d.queued) || 0,
    rejected: Number(d.rejected) || 0,
    error: Number(d.error) || 0,
    failed: Number(d.failed) || 0,
  }));

export const whenLabel = (s) => (s ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

const col = (key, labelKey, width, fmt) => ({ key, width, format: fmt, get label() { return t(`columns.${labelKey}`); } });
export const EXPORT_COLUMNS = [
  col("createdAt", "when", 18, (v) => (v ? new Date(v).toLocaleString("pt-BR") : "")),
  col("whatsappName", "connection", 18, (_, r) => r.whatsapp?.name || ""),
  col("origin", "origin", 20, (v) => originLabel(v)),
  col("route", "route", 16),
  col("kind", "kind", 12, (v) => kindLabel(v)),
  col("number", "number", 16),
  col("statusCode", "status", 8),
  col("outcome", "outcome", 16, (v) => outcomeMeta(v).label),
  col("durationMs", "latency", 10, (v) => formatMs(v)),
  col("errorMessage", "error", 50, (v) => v || ""),
  col("ip", "ip", 16),
  col("userAgent", "userAgent", 30, (v) => v || ""),
  col("ticketId", "actions", 10, (v) => v || ""),
];

export const buildExportRows = (rows, columns) =>
  (rows || []).map((r) => columns.map((c) => (c.format ? c.format(r[c.key], r) : r[c.key] ?? "")));

export const pageRange = (page, size, total) => {
  if (!total) return t("noResults");
  const from = (page - 1) * size + 1;
  return t("showing", { from, to: Math.min(page * size, total), total });
};
